import React from 'react'
import { useNavigate, useLocation, useParams, Location, NavigateFunction } from 'react-router-dom'

/**
 * Compatibility layer for React Router v5 → v6 migration.
 * Provides a withRouter HOC and RouteComponentProps type
 * so class components continue working without rewriting.
 */

export interface RouteComponentProps {
  history: {
    push: (path: string, state?: any) => void
    replace: (path: string, state?: any) => void
    goBack: () => void
    location: Location & { state: any }
  }
  location: Location & { state: any }
  match: { params: Record<string, string | undefined> }
}

export function withRouter<P extends object>(
  Component: React.ComponentType<P>
) {
  function WithRouterWrapper(props: Omit<P, keyof RouteComponentProps>) {
    const navigate: NavigateFunction = useNavigate()
    const location = useLocation() as Location & { state: any }
    const params = useParams()

    const history = {
      push: (path: string, state?: any) => navigate(path, { state }),
      replace: (path: string, state?: any) => navigate(path, { replace: true, state }),
      goBack: () => navigate(-1),
      location,
    }

    return (
      <Component
        {...(props as P)}
        history={history}
        location={location}
        match={{ params }}
      />
    )
  }

  const displayName = Component.displayName || Component.name || 'Component'
  WithRouterWrapper.displayName = `withRouter(${displayName})`

  return WithRouterWrapper
}
